import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Shield, Users, GraduationCap, Building2, UserCheck, BookOpen } from "lucide-react";

const ROLE_CONFIG: Record<string, { label: string; description: string; icon: React.ElementType }> = {
  master_admin: { label: "Master Admin", description: "Full platform administration", icon: Shield },
  admin: { label: "Associate", description: "Manage users, courses & settings", icon: Shield },
  state_partner: { label: "State Partner", description: "Oversee centres in your state", icon: Building2 },
  growth_partner: { label: "Growth Partner", description: "Oversee growth across your network", icon: Building2 },
  center_manager: { label: "Centre Manager", description: "Manage your learning centre", icon: Building2 },
  tutor: { label: "Tutor", description: "Teach and track student progress", icon: GraduationCap },
  parent: { label: "Parent", description: "Monitor your child's learning", icon: Users },
  student: { label: "Student", description: "Access courses and assessments", icon: BookOpen },
};

interface RoleSelectionDialogProps {
  open: boolean;
  roles: string[];
  onSelectRole: (role: string) => void;
}

export function RoleSelectionDialog({ open, roles, onSelectRole }: RoleSelectionDialogProps) {
  return (
    <Dialog open={open}>
      <DialogContent
        className="sm:max-w-md [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="text-center">
          <DialogTitle className="text-xl">Choose Your Role</DialogTitle>
          <DialogDescription>
            You have multiple roles on this platform. Please select which role you'd like to use for this session.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 mt-4">
          {roles.map((role) => {
            const config = ROLE_CONFIG[role] || { label: role, description: "", icon: UserCheck };
            const Icon = config.icon;
            return (
              <Button
                key={role}
                variant="outline"
                className="h-auto p-4 flex items-center gap-4 justify-start hover:bg-primary/5 hover:border-primary transition-all"
                onClick={() => onSelectRole(role)}
              >
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <div className="text-left">
                  <div className="font-semibold">{config.label}</div>
                  <div className="text-sm text-muted-foreground">{config.description}</div>
                </div>
              </Button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
